import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Observable, Subscription } from 'rxjs';

import { Dietitian } from '../../../../../../data/dietitian/domain/dietitian';
import { MessageTemplate } from '../../../../../../data/dietitian/domain/message_template/message-template';
import { MessageTemplateCommands } from '../../../../../../data/dietitian/domain/message_template/message-template-commands';
import { PatientGroup } from '../../../../../../data/dietitian/domain/patient_group/patient_group';
import { Template } from '../../../../../../data/dietitian/domain/templates/template';
import { Patient } from '../../../../../../data/patient/domain/patient';
import { PatientInfoViewModel } from '../send-question-modal/send-question-modal.facade';
import { SendSurveyModalFacade } from './send-survey-modal-facade.service';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'msd-send-survey-modal',
  templateUrl: './send-survey-modal.component.html',
  styleUrls: ['./send-survey-modal.component.scss'],
  providers: [SendSurveyModalFacade],
})
export class SendSurveyModalComponent implements OnInit, OnDestroy {
  @Output() send = new EventEmitter();
  @Input() target: Patient | Patient[] | PatientGroup | undefined;
  public isLoading = false;
  public error?: string;
  public dietitian: Dietitian;
  public templates: Template[];
  public currentTemplate: Template | undefined = undefined;
  public invalidMessageTemplate = false;
  public isScheduled = false;
  public scheduledDate: Date | undefined;
  public scheduledTime: string | undefined;
  private patientStatusSubscription?: Subscription;
  private viewModel$: Observable<PatientInfoViewModel>;
  private currentMessageTemplate: MessageTemplate;

  constructor(
    private bsModalRef: BsModalRef,
    private facade: SendSurveyModalFacade,
    private messageTemplateCommands: MessageTemplateCommands,
    private modalService: BsModalService,
  ) {
    this.viewModel$ = this.facade.viewModel$;
  }

  ngOnInit(): void {
    this.patientStatusSubscription = this.viewModel$.subscribe((state) => {
      this.dietitian = state.dietitian;
      this.facade
        .getDietitianActiveSurvey(this.dietitian)
        .then((survey) => (this.templates = survey));
    });
  }

  ngOnDestroy(): void {
    this.patientStatusSubscription?.unsubscribe();
  }

  get name() {
    if (this.target) {
      if (Array.isArray(this.target)) {
        if (this.target.length > 1) {
          return (
            'à une sélection de ' +
            this.target.length +
            ' patient' +
            (this.target.length > 1 ? 's' : '')
          );
        } else {
          return 'à ' + this.target[0].fullName;
        }
      } else {
        if (this.target instanceof PatientGroup) {
          return 'au groupe ' + this.target?.name;
        } else {
          return 'à ' + this.target?.fullName;
        }
      }
    } else {
      return 'à tous les patients';
    }
  }

  get surveyInvalid(): boolean {
    return !this.currentTemplate;
  }

  hideModal() {
    this.send.emit();
    this.bsModalRef.hide();
  }

  async sendSurvey() {
    // Sauvegarde du modèle de message
    if (this.currentMessageTemplate.id.toValue() === -1) {
      this.currentMessageTemplate =
        await this.messageTemplateCommands.saveTemplate(
          this.dietitian.id.toString(),
          'survey',
          this.currentMessageTemplate,
        );
    } else if (this.currentMessageTemplate.id.toValue() !== 0) {
      const id: number = this.currentMessageTemplate.id.toValue() as number;
      this.currentMessageTemplate =
        await this.messageTemplateCommands.updateTemplate(
          this.dietitian.id.toString(),
          this.currentMessageTemplate,
          id,
        );
    }
    if (this.target) {
      if (Array.isArray(this.target)) {
        await this.sendSurveyToPatientSelection();
      } else {
        if (this.target instanceof PatientGroup) {
          await this.sendSurveyToGroupedPatients();
        } else {
          await this.sendSurveyToSpecificPatient();
        }
      }
    } else {
      await this.sendSurveyToGroupedPatients();
    }
  }

  private async sendSurveyToSpecificPatient() {
    if (
      !this.currentTemplate ||
      !(this.target instanceof Patient) ||
      !this.dietitian
    ) {
      return;
    }

    this.error = undefined;
    this.isLoading = true;

    try {
      if (this.isScheduled && this.scheduledDate && this.scheduledTime) {
        await this.facade.scheduleSurveyToSpecificPatient(
          this.dietitian,
          this.currentTemplate,
          this.getMessageTemplateContent(),
          this.target,
          this.scheduledDate,
          this.scheduledTime,
        );
      } else {
        await this.facade.sendSurveyToSpecificPatient(
          this.currentTemplate,
          this.getMessageTemplateContent(),
          this.target,
        );
        this.send?.emit();
      }
      this.hideModal();
    } catch (err: any) {
      this.error = err;
    } finally {
      this.isLoading = false;
    }
  }

  private async sendSurveyToGroupedPatients() {
    if (!this.currentTemplate || !this.dietitian) {
      return;
    }
    this.error = undefined;
    this.isLoading = true;
    try {
      if (this.isScheduled && this.scheduledDate && this.scheduledTime) {
        await this.facade.scheduleSurveyToGroupedPatients(
          this.dietitian,
          this.currentTemplate,
          this.getMessageTemplateContent(),
          this.target as PatientGroup,
          this.scheduledDate,
          this.scheduledTime,
        );
      } else {
        await this.facade.sendSurveyToGroupedPatients(
          this.currentTemplate,
          this.getMessageTemplateContent(),
          this.target as PatientGroup,
        );
        this.send?.emit();
      }
      this.hideModal();
    } catch (err: any) {
      this.error = err;
    } finally {
      this.isLoading = false;
    }
  }

  onChangeMessageTemplate(template: MessageTemplate) {
    this.currentMessageTemplate = template;
  }

  private getMessageTemplateContent(): string {
    return (
      this.currentMessageTemplate.content +
      (this.currentMessageTemplate.withSign
        ? '\n\n' + this.messageTemplateCommands.getSign(this.dietitian)
        : '')
    );
  }

  private async sendSurveyToPatientSelection() {
    if (
      !this.currentTemplate ||
      !this.dietitian ||
      !Array.isArray(this.target)
    ) {
      return;
    }
    this.error = undefined;
    this.isLoading = true;
    try {
      if (this.isScheduled && this.scheduledDate && this.scheduledTime) {
        await this.facade.scheduleSurveyToPatientSelection(
          this.dietitian,
          this.currentTemplate,
          this.getMessageTemplateContent(),
          this.target,
          this.scheduledDate,
          this.scheduledTime,
        );
      } else {
        await this.facade.sendSurveyToPatientSelection(
          this.currentTemplate,
          this.getMessageTemplateContent(),
          this.target,
        );
        this.send?.emit();
      }
      this.hideModal();
    } catch (err: any) {
      this.error = err;
    } finally {
      this.isLoading = false;
    }
  }

  enableScheduling() {
    if (this.isScheduled) {
      return (
        this.scheduledDate &&
        this.scheduledTime &&
        this.scheduledTime.length > 0
      );
    }
    return true;
  }

  onDeleteMessageTemplate(template: MessageTemplate) {
    this.messageTemplateCommands
      .deleteTemplate(this.dietitian.id.toString(), template)
      .then();
  }
}
